<template>
    <div
        ref="datePickerRoot"
        class="date-picker-wrapper"
        @click.stop
        @focusin.stop
        @keydown.stop
        @input.stop
    >
        <input
            v-bind="forwardedAttrs"
            ref="datePickerInput"
            type="search"
            :id="elemId"
            :class="['form-control', $attrs.class, 'date-picker-input']"
            :placeholder="localPlaceholder"
            :value="displayValue"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            readonly
            @click="OpenDatePicker"
            @focus="OpenDatePicker"
            @keydown="OnInputKeydown"
        />

        <div v-if="isOpen" class="date-picker-popup" @click.stop>
            <div class="date-picker-controls">
                <button type="button" class="date-picker-arrow" @click="MoveMonth(-1)">
                    <i class="fa-solid fa-caret-left"></i>
                </button>
                <button type="button" class="date-picker-month" @click="GoToToday">
                    {{ monthLabel }} {{ currentYear }}
                </button>
                <button type="button" class="date-picker-arrow" @click="MoveMonth(1)">
                    <i class="fa-solid fa-caret-right"></i>
                </button>
            </div>

            <div class="date-picker-grid date-picker-grid--days">
                <span v-for="day in dayLabels" :key="day">{{ day }}</span>
            </div>

            <div class="date-picker-grid">
                <span
                    v-for="blank in leadingBlanks"
                    :key="`blank-${blank}`"
                    class="date-picker-day date-picker-day--blank"
                ></span>
                <button
                    v-for="day in monthDays"
                    :key="day.iso"
                    type="button"
                    class="date-picker-day"
                    :class="{
                        'is-today': day.isToday,
                        'is-selected': day.isSelected
                    }"
                    @click="SelectDate(day.date)"
                >
                    {{ day.label }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {ReformatDate} from '@/helpers/utilities.js'

export default {
    inheritAttrs: false,
    mounted(){
        this.SyncFromModel(this.modelValue)
        document.addEventListener('mousedown', this.OnDocumentMouseDown)
    },
    beforeUnmount(){
        document.removeEventListener('mousedown', this.OnDocumentMouseDown)
    },
    methods : {
        OpenDatePicker(){
            this.SyncFromModel(this.modelValue)
            this.isOpen = true
        },
        CloseDatePicker(){
            this.isOpen = false
        },
        OnInputKeydown(e){
            if(e.key === 'Tab'){
                this.CloseDatePicker()
                return
            }

            if(e.key === 'Escape'){
                e.preventDefault()
                this.CloseDatePicker()
                return
            }

            if(e.key === 'Enter' || e.key === ' '){
                e.preventDefault()
                this.OpenDatePicker()
                return
            }

            e.preventDefault()
        },
        OnDocumentMouseDown(e){
            if(!this.$refs.datePickerRoot || this.$refs.datePickerRoot.contains(e.target)){
                return
            }

            this.CloseDatePicker()
        },
        SelectDate(date){
            this.selectedDate = this.StripTime(date)
            this.$emit('update:modelValue', ReformatDate(this.selectedDate, 'YYYY-MM-DD'))
            this.CloseDatePicker()
        },
        MoveMonth(direction){
            const nextMonth = new Date(this.currentYear, this.currentMonth + direction, 1)
            this.currentYear = nextMonth.getFullYear()
            this.currentMonth = nextMonth.getMonth()
        },
        GoToToday(){
            const baseDate = this.selectedDate || new Date()
            this.currentYear = baseDate.getFullYear()
            this.currentMonth = baseDate.getMonth()
        },
        SyncFromModel(value){
            const parsedDate = this.ParseDate(value)
            const baseDate = parsedDate || this.selectedDate || new Date()

            this.selectedDate = parsedDate
            this.currentYear = baseDate.getFullYear()
            this.currentMonth = baseDate.getMonth()
        },
        ParseDate(value){
            if(!value){
                return null
            }

            const parsedDate = new Date(value)
            if(Number.isNaN(parsedDate.getTime())){
                return null
            }

            return this.StripTime(parsedDate)
        },
        StripTime(date){
            return new Date(date.getFullYear(), date.getMonth(), date.getDate())
        },
        IsSameDate(left, right){
            if(!left || !right){
                return false
            }

            return left.getFullYear() === right.getFullYear()
                && left.getMonth() === right.getMonth()
                && left.getDate() === right.getDate()
        }
    },
    watch : {
        modelValue(val){
            this.SyncFromModel(val)
        }
    },
    computed :{
        forwardedAttrs(){
            const attrs = {...this.$attrs}
            delete attrs.class
            return attrs
        },
        localPlaceholder() {
            return this.placeholder ==  'Please Select a Date'  ? this.$i18n.t('Please Select a Date') :this.placeholder
        },
        displayValue(){
            return this.selectedDate ? ReformatDate(this.selectedDate) : ''
        },
        monthLabel(){
            return this.monthLabels[this.currentMonth]
        },
        monthLabels(){
            return [
                this.$i18n.t('date picker component.January'),
                this.$i18n.t('date picker component.February'),
                this.$i18n.t('date picker component.March'),
                this.$i18n.t('date picker component.April'),
                this.$i18n.t('date picker component.May'),
                this.$i18n.t('date picker component.June'),
                this.$i18n.t('date picker component.July'),
                this.$i18n.t('date picker component.August'),
                this.$i18n.t('date picker component.September'),
                this.$i18n.t('date picker component.October'),
                this.$i18n.t('date picker component.November'),
                this.$i18n.t('date picker component.December')
            ]
        },
        dayLabels(){
            return [
                this.$i18n.t('date picker component.Sunday'),
                this.$i18n.t('date picker component.Monday'),
                this.$i18n.t('date picker component.Tuesday'),
                this.$i18n.t('date picker component.Wednesday'),
                this.$i18n.t('date picker component.Thursday'),
                this.$i18n.t('date picker component.Friday'),
                this.$i18n.t('date picker component.Saturday'),
            ].map((day) => day.slice(0, 2))
        },
        leadingBlanks(){
            return new Date(this.currentYear, this.currentMonth, 1).getDay()
        },
        monthDays(){
            const numberOfDays = new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
            const today = this.StripTime(new Date())

            return Array.from({ length: numberOfDays }, (_, index) => {
                const date = new Date(this.currentYear, this.currentMonth, index + 1)
                return {
                    date,
                    iso: ReformatDate(date, 'YYYY-MM-DD'),
                    label: index + 1,
                    isToday: this.IsSameDate(date, today),
                    isSelected: this.IsSameDate(date, this.selectedDate)
                }
            })
        }
    },
    props: {
        modelValue : {
            type : String,
        },
        elemId : {
            type : String,
        },
        placeholder : {
            type : String,
            default : 'Please Select a Date'
        }
    },
    data (){
        return {
            selectedDate : null,
            currentMonth : new Date().getMonth(),
            currentYear : new Date().getFullYear(),
            isOpen: false
        }
    }
}
</script>

<style scoped>
.date-picker-wrapper {
    position: relative;
    width: 100%;
}

.date-picker-input {
    width: 100%;
    min-height: 3.125rem;
    border: 1px solid #d9d9d9;
    border-radius: 0;
    padding: 0.65rem 0.85rem;
    color: #1f1f1f;
    font-size: 0.9rem;
    line-height: 1.5;
    box-shadow: none;
    cursor: pointer;
    background-color: #ffffff;
}

.date-picker-input:focus {
    border-color: #d9d9d9;
    box-shadow: none;
}

.date-picker-popup {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    width: 14.5rem;
    border: 1px solid #8d8d8d;
    background: #ffffff;
    box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.15);
    color: #1f1f1f;
    font-size: 0.82rem;
    z-index: 10050;
}

.date-picker-controls {
    display: grid;
    grid-template-columns: 2rem minmax(0, 1fr) 2rem;
    align-items: center;
    min-height: 2rem;
    border-bottom: 1px solid #bcbcbc;
    background: #d6d6d6;
}

.date-picker-arrow,
.date-picker-month,
.date-picker-day {
    border: 0;
    background: transparent;
    color: inherit;
}

.date-picker-arrow {
    min-height: 2rem;
    color: #777777;
}

.date-picker-month {
    min-height: 2rem;
    font-weight: 700;
    text-align: center;
}

.date-picker-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 0;
    padding: 0.25rem 0.4rem 0.4rem;
}

.date-picker-grid--days {
    padding-bottom: 0;
    color: #777777;
    font-weight: 700;
    text-align: center;
}

.date-picker-day {
    min-width: 0;
    min-height: 1.65rem;
    padding: 0;
    line-height: 1.65rem;
    text-align: center;
}

.date-picker-day:hover,
.date-picker-day:focus {
    background: #eeeeee;
    outline: 0;
}

.date-picker-day.is-today {
    color: var(--lens-accent, #b0005a);
    font-weight: 700;
}

.date-picker-day.is-selected {
    background: color-mix(in srgb, var(--lens-accent, #b0005a) 18%, #ffffff);
    color: var(--lens-accent, #b0005a);
    font-weight: 800;
}

.date-picker-day--blank {
    min-height: 1.65rem;
}
</style>
